import { Box, Drawer, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import AppButton from '../core/buttons';
import AppInputLabel from '../core/label';
import AppInput from '../core/outlinedInput';
import FormItemRow from '../forms/formRow';
import { useTranslation } from 'react-i18next';
import docsRelease from '../../api/docsRelease';
import softsRelease from '../../api/softsRelease';
import { useHistory } from 'react-router';
import IDocProduct from '../../types/docRelease.interface';
import LoadingFile from '../loadingFile';
import FileCard from '../fileCard';
import { FileDropZone } from '../fileDropZone';
import IFile from '../../types/file.interface';

interface MaterialProductDrawerProps {
  updateTarget: IDocProduct;
  onClose: () => void;
  isDelete?: boolean;
  isSoftware?: boolean;
}

export const MaterialEditProductDrawer: React.FC<
  MaterialProductDrawerProps
> = ({ updateTarget, onClose, isDelete, isSoftware }) => {
  const [title, setTitle] = useState(updateTarget.title);
  const [sortIndex, setSortIndex] = useState(updateTarget.sortIndex);

  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const history = useHistory();
  const [picture, setPicture] = useState<IFile | undefined>(
    updateTarget.picture
  );
  const [files, setFiles] = useState<Array<File>>([]);
  
  const repository = isSoftware ? softsRelease : docsRelease;
  const [repoKey, setRepoKey] = useState<String>(isSoftware ? "software" : "docs");
  const [urlPart, setUrlPart] = useState<String>(isSoftware ? "software" : "documentation");

  const productEdit = useMutation(`${repoKey}-release`, repository.update, {
    onSuccess: (product) => {
      qc.invalidateQueries([`${repoKey}-releases`]);
      history.push(`/${urlPart}/${product!.id}`);
      onClose();
    },
    onError: (err) => {
      //@ts-ignore
      enqueueSnackbar((err as any).response.data.errors[0].reason, {
        variant: 'error',
      });
    },
  });

  const productDelete = useMutation(`${repoKey}-release`, repository.delete, {
    onSuccess: () => {
      qc.invalidateQueries([`${repoKey}-releases`]);
      history.push(`/${urlPart}`);
      onClose();
    },
    onError: (err) => {
      //@ts-ignore
      enqueueSnackbar((err as any).response.data.errors[0].reason, {
        variant: 'error',
      });
    },
  });

  const handleEditProduct = () => {
    qc.invalidateQueries([`${repoKey}-releases`]);
    productEdit.mutate({
      id: updateTarget.id,
      title,
      picture,
      sortIndex,
    });
  };
  const handleDeleteProduct = () => {
    if (!confirm(t('page.docs.deleteCheck'))) {
      return;
    }

    productDelete.mutate({ target: updateTarget });
  };

  const handleLoadSuccess = (fileResponse: IFile, f: File) => {
    const nf = files.filter((file) => file.name !== f.name);
    setFiles(nf);
    setPicture(fileResponse);
  };

  const handleFileDelete = () => {
    setPicture(undefined);
  };

  const handleNewFiles = async (list: FileList) => {
    for (let i = 0; i < list.length; i++) {
      setFiles((files) => [...files, list[i]]);
    }
  };

  return (
    <Drawer
      onBackdropClick={onClose}
      onClose={onClose}
      open={true}
      anchor='right'
      PaperProps={{
        style: { boxShadow: '0px 4px 20px rgba(15, 4, 139, 0.11)', width: 487 },
      }}
      ModalProps={{
        hideBackdrop: false,
        BackdropProps: {
          invisible: true,
        },
      }}
    >
      <Box mx={3} mt={3}>
        <Box mb={2}>
          <Typography variant='h3'>
            {t('component.addProductDrawer.editHeadline')}
          </Typography>
        </Box>

        <Box>
          {files.map((f, i) => (
            <LoadingFile
              onLoadSuccess={(fileResponse) =>
                handleLoadSuccess(fileResponse, f)
              }
              key={f.name + i}
              file={f}
            />
          ))}
          {picture ? (
            <FileCard
              key={picture.id}
              file={picture}
              onDelete={handleFileDelete}
            />
          ) : (
            <Box height={284}>
              <FileDropZone
                onChange={handleNewFiles}
                allowedFileExtensions={[]}
                allowedMimeTypes={[]}
              />
            </Box>
          )}
        </Box>

        <Box display='flex' flexDirection='column' mt={3}>
          <FormItemRow>
            <AppInputLabel>
              {t('component.addProductDrawer.title')}
            </AppInputLabel>

            <AppInput
              defaultValue={updateTarget.title}
              onChange={(v) => setTitle(v.target.value)}
            />
          </FormItemRow>

          <FormItemRow>
            <AppInputLabel>
              {t('component.addProductDrawer.sort')}
            </AppInputLabel>

            <AppInput
              onChange={(v) => setSortIndex(Number(v.target.value) ?? 1)}
              defaultValue={sortIndex}
              type='number'
            />
          </FormItemRow>

          <Box mt={1}>
            <FormItemRow>
              <AppInputLabel />

              <AppButton
                disabled={!title || !sortIndex}
                variant='contained'
                color='primary'
                onClick={handleEditProduct}
              >
                {t('button.save')}
              </AppButton>

              {(updateTarget && isDelete) && (
                <AppButton
                  variant='contained'
                  color='secondary'
                  onClick={handleDeleteProduct}
                  style={{ marginLeft: 5 }}
                >
                  {t('button.delete')}
                </AppButton>
              )}
            </FormItemRow>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
